import intl from "react-intl-universal";

import {readFileContent} from "../../Editor/utils/files/readFileContent";
import {AppState} from "@stores/AppState";
import { createItem } from "../item/createItem";
import {logErrorToSentry} from "../../utils/errors/logErrorToSentry";
import {readFileContentAsBuffer} from "../../Editor/utils/files/readFileContentAsBuffer";
import {uploadFileToS3} from "../../utils/file/ImageUploadLogic";
import { IStorageService } from "../../platform/services/storage/StorageService";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";
import { WorkspaceModelType } from "@stores/WorkspaceStore";


import {snackBarRef} from "@stores/snackbarRef";


const createImportedDoc = (
    services: CrudServices,
    workspace: WorkspaceModelType,
    appState: AppState,
    fileName: string,
    content: string
) => {
    return createItem(services, {
        workspace: workspace,
        appState: appState,
        name: fileName,
        content: content,
        noSelect: true,
    });
};

export type ImportMode = 'document' | 'image';

export const supportedDocumentTypes = [
    'text/plain',
    'application/rtf',
    'text/markdown',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

export const supportedImageTypes = [
    'image/jpeg',
    'image/png',
    'image/jpg',
];

export const importFile = async (
    services: CrudServices,
    workspace: WorkspaceModelType,
    appState: AppState,
    file: File,
    storageService?: IStorageService,
    tenantId?: string,
    mode: ImportMode = 'document'
) => {
    const enqueueSnackbar = snackBarRef.current!.enqueueSnackbar;

    try {
        if (mode === 'image') {
            if (supportedImageTypes.includes(file.type)) {
                if (!storageService || !tenantId) {
                    throw new Error('Storage service and tenant ID are required for image import');
                }
                const {s3FileName} = await uploadFileToS3(file, storageService, tenantId);
                return s3FileName;
            } else {
                enqueueSnackbar({
                    message: intl.get('import_doc.unsupported_image_format', {format: file.name}).d(`Unsupported image format: “{format}”`),
                    variant: 'error',
                });
                return;
            }
        }

        if (mode === 'document') {
            const ext = file.name.split('.').pop()?.toLowerCase();
            const fileType = file.type || (ext === 'md' ? 'text/markdown' : ext === 'rtf' ? 'application/rtf' : 'text/plain');
            switch (fileType) {
                case 'text/plain':
                case 'application/rtf':
                case 'text/markdown': {
                    try {
                        const fileText = await readFileContent(file);
                        const module = await import('./importMarkdown');
                        const parsedDoc = module.parseMarkdown(fileText);
                        return await createImportedDoc(services, workspace, appState, file.name, parsedDoc.toJSON());
                    } catch (chunkError: any) {
                        if (chunkError.message?.includes('Failed to fetch') || chunkError.message?.includes('404')) {
                            throw new Error(`Chunk load failed for markdown parser (${chunkError.message}). A page reload is needed.`);
                        }
                        throw chunkError;
                    }
                }
                case 'application/msword':
                case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
                    try {
                        const fileAsArrayBuffer = await readFileContentAsBuffer(file);
                        const module = await import('./importDoc');
                        const parsedDoc = await module.parseDoc(fileAsArrayBuffer, storageService, tenantId);
                        return await createImportedDoc(services, workspace, appState, file.name, parsedDoc.toJSON());
                    } catch (chunkError: any) {
                        if (chunkError.message?.includes('Failed to fetch') || chunkError.message?.includes('404')) {
                            throw new Error(`Chunk load failed for document parser (${chunkError.message}). A page reload is needed.`);
                        }
                        throw chunkError;
                    }
                }
                default: {
                    enqueueSnackbar({
                        message: intl.get('import_doc.unsupported_format', {format: file.name}).d(`Unsupported import format: “{format}”`),
                        variant: 'error',
                    });
                }
            }
        }
    } catch (error: any) {
        logErrorToSentry(error);
        enqueueSnackbar({
            message: intl.get('import_doc.error', {file: file.name}).d(`Import failed for “{file}” file`),
            variant: 'error',
        });
    }
};
